import type { ExperienceEventFragment } from '@/features/events/fragments/ExperienceEventFragment.generated';
import { BlockTypes, type InteractionApp } from '@/types/gql.generated';
import type { PageExperienceFragment } from '../../experiences/fragments/PageExperience.generated';
import type { DecoratedEvent, DecoratedExperienceEvent } from '../types';
import { createDecoratedMagicEvent } from './createDecoratedMagicEvent';

type ExperienceLike = Partial<
  Pick<
    PageExperienceFragment,
    'blocks' | 'interactionSlots' | 'interactionApps'
  >
>;

// The interaction apps shown on each event card depend only on the experience
// (its collection block + interaction slots), not on the individual event, so
// they can be computed once per experience and applied to every event.
export const getExperienceInteractionApps = (
  experience: ExperienceLike
): (InteractionApp | null)[] => {
  const collectionBlock = experience.blocks?.find(
    (block) => block.type === BlockTypes.Collection
  );
  const interactionSlots =
    experience.interactionSlots?.filter(
      (slot) => slot.blockId === collectionBlock?.id
    ) ?? [];
  return interactionSlots.map(
    (slot) =>
      experience.interactionApps?.find(
        (app) => app.id === slot.interactionAppId
      ) ?? null
  );
};

export const createDecoratedExperienceEvent = (
  experienceEvent: ExperienceEventFragment,
  experience: ExperienceLike,
  displayTimezone: string
): DecoratedExperienceEvent => {
  const interactionApps = getExperienceInteractionApps(experience);

  return {
    ...experienceEvent,
    ...createDecoratedMagicEvent(experienceEvent, displayTimezone),
    interactionApps,
  };
};

// Augments an already-decorated event into an experience-shaped event without
// re-decoding dates. Staged chat events lack the experience-only fields
// (origin/metadata/interactionApps), so they fail `isExperienceEvent` and hide
// experience-only controls (e.g. category). Applying the experience's
// interaction apps and a default metadata object lets those controls render
// while a category is set before the events are imported into the experience.
export const asExperienceEvent = (
  event: DecoratedEvent,
  experience: ExperienceLike
): DecoratedExperienceEvent => ({
  ...event,
  origin: 'origin' in event ? event.origin : null,
  isSubscribed: 'isSubscribed' in event ? event.isSubscribed : false,
  exportedToExperiences:
    'exportedToExperiences' in event ? event.exportedToExperiences : [],
  // Backfill any missing metadata fields so every event has a complete,
  // consistent shape regardless of where it originated.
  metadata: {
    category: null,
    hidden: [],
    inheritedHidden: [],
    labels: [],
    whoLabels: [],
    ...('metadata' in event ? event.metadata : undefined),
  },
  interactionApps: getExperienceInteractionApps(experience),
});
